import { BRAND, CHART_COLORS } from '@verlo/shared/lib/brand';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import {
  Share2, Calendar,
  BarChart3, LineChart, PieChart, Table2, Sigma, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Button } from '@verlo/shared/components/ui/button';
import { reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import SavedViewChips from '../../../components/reports/SavedViewChips';
import { changedSinceSaved } from '../../../lib/savedViews';
import { formatMetric, formatRange } from '../../../components/reports/formatters';
import { Sparkline } from '../../../components/reports/Sparkline';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

const CHART_ICONS = {
  kpi:   { icon: Sigma,       label: 'KPI tile' },
  table: { icon: Table2,      label: 'Table' },
  line:  { icon: LineChart,   label: 'Line' },
  bar:   { icon: BarChart3,   label: 'Bar' },
  donut: { icon: PieChart,    label: 'Donut' },
};

const DONUT_COLORS = CHART_COLORS;

const emptyConfig = {
  metrics: ['revenue'],
  dimension: 'hour',
  chart_type: 'line',
  range: 'wtd',
  site: '',
};

// ----- Chart components -----

const TableChart = ({ series, summary }) => (
  <div className="border border-hairline rounded-lg overflow-hidden bg-white" data-testid="builder-chart-table">
    <table className="w-full text-[12px]">
      <thead className="bg-surface-2">
        <tr>
          <th className="text-left px-3 py-2 font-semibold text-ink-soft">Bucket</th>
          {series.map((s) => (
            <th key={s.metric} className="text-right px-3 py-2 font-semibold text-ink-soft">{s.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(series[0]?.buckets || []).map((_, i) => (
          <tr key={i} className="border-t border-hairline">
            <td className="px-3 py-2 text-ink-soft">{series[0].buckets[i].label}</td>
            {series.map((s, c) => (
              <td key={s.metric} className="px-3 py-2 text-right font-mono-display text-ink" data-testid={`builder-cell-${i}-${c}`}>
                {formatMetric(s.buckets[i]?.value, s.format)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-surface-2/50">
        <tr className="border-t border-hairline">
          <td className="px-3 py-2 font-semibold text-ink-soft text-[11px] uppercase tracking-wider">Total / Avg</td>
          {summary.map((s) => (
            <td key={s.metric} className="px-3 py-2 text-right font-mono-display font-bold text-verlo">
              {formatMetric(s.value, s.format)}
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  </div>
);

const LineBarChart = ({ series, kind = 'line' }) => {
  // Multi-series, scales each series to its own axis range
  const w = 720, h = 240, pad = 36;
  const buckets = series[0]?.buckets || [];
  if (!buckets.length) return null;
  const colors = CHART_COLORS;

  return (
    <div className="border border-hairline rounded-lg p-4 bg-white" data-testid={`builder-chart-${kind}`}>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[260px]">
        {/* Y gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line key={i} x1={pad} x2={w - 8} y1={pad + (h - pad * 2) * t} y2={pad + (h - pad * 2) * t}
                stroke="#e2e8f0" strokeDasharray="2 4" />
        ))}
        {series.map((s, si) => {
          const values = s.buckets.map((b) => b.value);
          const max = Math.max(...values, 0.01);
          const min = Math.min(...values, 0);
          const range = max - min || 1;
          const step = (w - pad - 8) / Math.max(buckets.length - 1, 1);
          const points = values.map((v, i) => {
            const x = pad + i * step;
            const y = pad + (h - pad * 2) * (1 - (v - min) / range);
            return [x, y];
          });
          if (kind === 'bar') {
            const bw = Math.max(step * 0.55 / series.length, 4);
            return (
              <g key={s.metric}>
                {points.map(([x, y], i) => (
                  <rect
                    key={i}
                    x={x - bw / 2 + si * bw * 1.05 - ((series.length - 1) * bw * 1.05) / 2}
                    y={y}
                    width={bw}
                    height={h - pad - y}
                    fill={colors[si % colors.length]}
                    opacity={0.85}
                    rx={2}
                  />
                ))}
              </g>
            );
          }
          // line
          const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
          return (
            <g key={s.metric}>
              <path d={path} stroke={colors[si % colors.length]} strokeWidth={2} fill="none" strokeLinecap="round" />
              {points.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r={3} fill="white" stroke={colors[si % colors.length]} strokeWidth={1.5} />
              ))}
            </g>
          );
        })}
        {/* X labels */}
        {buckets.map((b, i) => {
          const step = (w - pad - 8) / Math.max(buckets.length - 1, 1);
          if (buckets.length > 10 && i % 2 !== 0) return null;
          return (
            <text key={i} x={pad + i * step} y={h - 10} textAnchor="middle" fontSize="9" fill="#64748b">
              {b.label}
            </text>
          );
        })}
      </svg>
      <div className="flex gap-3 mt-2 flex-wrap">
        {series.map((s, i) => (
          <div key={s.metric} className="flex items-center gap-1.5 text-[12.5px] text-ink-soft">
            <span className="w-2.5 h-2.5 rounded-md" style={{ background: colors[i % colors.length] }} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const DonutChart = ({ series }) => {
  // Use the first metric only — donut shows proportions across one dimension
  const s = series[0];
  if (!s) return null;
  const buckets = s.buckets;
  const total = buckets.reduce((sum, b) => sum + Math.max(b.value, 0), 0) || 1;
  let cum = 0;
  const r = 70;
  const cx = 90, cy = 90;
  const slices = buckets.map((b, i) => {
    const frac = Math.max(b.value, 0) / total;
    const startA = cum * 2 * Math.PI - Math.PI / 2;
    const endA = (cum + frac) * 2 * Math.PI - Math.PI / 2;
    cum += frac;
    const x1 = cx + r * Math.cos(startA), y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA),   y2 = cy + r * Math.sin(endA);
    const large = frac > 0.5 ? 1 : 0;
    return {
      d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      fill: DONUT_COLORS[i % DONUT_COLORS.length],
      label: b.label,
      value: b.value,
      pct: (frac * 100).toFixed(1),
    };
  });
  return (
    <div className="border border-hairline rounded-lg p-4 bg-white flex items-center gap-6" data-testid="builder-chart-donut">
      <svg viewBox="0 0 180 180" className="w-44 h-44 shrink-0">
        {slices.map((sl, i) => <path key={i} d={sl.d} fill={sl.fill} stroke="white" strokeWidth={1.5} />)}
        <circle cx="90" cy="90" r="42" fill="white" />
        <text x="90" y="86" textAnchor="middle" fontSize="9" fill="#94a3b8" className="uppercase tracking-wider font-semibold">{s.label}</text>
        <text x="90" y="103" textAnchor="middle" fontSize="14" fill={BRAND.verlo} className="font-semibold font-mono">{formatMetric(total, s.format)}</text>
      </svg>
      <ul className="space-y-1.5 flex-1">
        {slices.map((sl, i) => (
          <li key={i} className="flex items-center justify-between gap-3 text-[12px]">
            <span className="flex items-center gap-2 text-ink-soft">
              <span className="w-2.5 h-2.5 rounded-md" style={{ background: sl.fill }} />
              {sl.label}
            </span>
            <span className="font-mono-display text-ink">{formatMetric(sl.value, s.format)} <span className="text-ink-faint">· {sl.pct}%</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const KpiTilesChart = ({ summary }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="builder-chart-kpi">
    {summary.map((s) => {
      const delta = s.delta_pct;
      const good = delta == null ? null : delta > 0;
      return (
        <div key={s.metric} className="bg-white border border-hairline rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-faint">{s.label}</div>
          <div className="text-[24px] leading-none font-mono text-ink mt-1">{formatMetric(s.value, s.format)}</div>
          {delta != null && (
            <div className={`text-[12.5px] mt-1 font-mono-display ${good ? 'text-signal-ok' : 'text-signal-stop'}`}>
              {good ? '+' : ''}{delta.toFixed(1)}% vs previous
            </div>
          )}
        </div>
      );
    })}
  </div>
);

// ----- Main page -----

// The saved view open last, opened again next time (per browser).
const LAST_VIEW_KEY = 'verlo.builder.lastView';

const ReportBuilderPage = () => {
  const [catalog, setCatalog] = useState({ metrics: [], dimensions: [], chart_types: [] });
  const [config, setConfig] = useState(emptyConfig);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [views, setViews] = useState([]);
  const [sites, setSites] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  // Schedule opens a new schedule for this report on the Schedules page; an unsaved report
  // is saved first, and the schedule opens once it has a name.
  const [scheduleAfterSave, setScheduleAfterSave] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canSchedule = hasPermission('reports.view_schedules');
  const schedule = (view) => navigate(`/back-office/reports/schedules?view=${view.id}`);
  const onSchedule = () => {
    if (activeView) { schedule(activeView); return; }
    setScheduleAfterSave(true);
    setSaveOpen(true);
  };
  const [activeView, setActiveView] = useState(null);

  const loadAll = async () => {
    const [c, v, s] = await Promise.all([
      reportsIntelApi.builderCatalog(),
      reportsIntelApi.listBuilderViews(),
      reportsIntelApi.listSites(),
    ]);
    setCatalog(c.data);
    setViews(v.data.views || []);
    setSites(s.data.sites || []);
    // A shared link (Share, below) opens the report it names.
    const shared = new URLSearchParams(window.location.search).get('view');
    const view = shared && (v.data.views || []).find((x) => x.id === shared && x.version !== 2);
    if (view) onLoad(view);
    else if (shared) toast.error('That report has been deleted, or was never saved');
    else {
      // The last saved view you had open opens again (D5).
      let last = null;
      try { last = localStorage.getItem(LAST_VIEW_KEY); } catch { /* storage off */ }
      const again = last && (v.data.views || []).find((x) => x.id === last && x.version !== 2);
      if (again) onLoad(again);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Auto-preview whenever config changes
  useEffect(() => {
    const run = async () => {
      if (!config.metrics.length) { setPreview(null); return; }
      // Skip invalid configs locally to avoid 400 noise
      if (['line', 'bar', 'donut'].includes(config.chart_type) && !config.dimension) {
        return;
      }
      setPreviewing(true);
      try {
        const res = await reportsIntelApi.previewBuilder(config);
        setPreview(res.data);
      } catch (e) {
        if (e.response?.status !== 400) toast.error("Couldn't preview");
        setPreview(null);
      } finally {
        setPreviewing(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [config]);

  const toggleMetric = (key) => {
    setConfig((c) => {
      const has = c.metrics.includes(key);
      const metrics = has ? c.metrics.filter((m) => m !== key) : [...c.metrics, key];
      return { ...c, metrics: metrics.length ? metrics : c.metrics };
    });
  };

  const onSave = async () => {
    if (!saveName.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const res = await reportsIntelApi.createBuilderView({ name: saveName.trim(), config });
      setViews((v) => [res.data, ...v]);
      setActiveView(res.data);
      toast.success('View saved');
      setSaveOpen(false);
      setSaveName('');
      if (scheduleAfterSave) schedule(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onLoad = (view) => {
    setConfig(view.config);
    setActiveView(view);
    try { localStorage.setItem(LAST_VIEW_KEY, view.id); } catch { /* storage off */ }
  };

  // Saved over: what's on screen becomes the open view.
  const onUpdate = async (view) => {
    try {
      const res = await reportsIntelApi.updateBuilderView(view.id, { config });
      setViews((vs) => vs.map((v) => (v.id === view.id ? res.data : v)));
      setActiveView(res.data);
      toast.success(`${view.name} updated`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update it"); }
  };
  const onRename = async (view, name) => {
    try {
      const res = await reportsIntelApi.updateBuilderView(view.id, { name });
      setViews((vs) => vs.map((v) => (v.id === view.id ? res.data : v)));
      if (activeView?.id === view.id) setActiveView(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rename it"); }
  };

  const onTogglePin = async (view) => {
    try {
      const res = await reportsIntelApi.updateBuilderView(view.id, { pinned: !view.pinned });
      setViews((vs) => vs.map((v) => (v.id === view.id ? res.data : v)));
      toast.success(res.data.pinned ? 'Pinned to Executive Dashboard' : 'Unpinned');
    } catch { toast.error('Could not update'); }
  };

  // The chip's menu asks first.
  const onDelete = async (view) => {
    await reportsIntelApi.deleteBuilderView(view.id);
    setViews((vs) => vs.filter((v) => v.id !== view.id));
    if (activeView?.id === view.id) setActiveView(null);
    toast.success('Deleted');
  };

  const onShare = () => {
    if (!activeView) { toast.error('Save the report first: a link opens a saved report'); return; }
    const link = `${window.location.origin}/back-office/reports/builder?view=${activeView.id}`;
    navigator.clipboard?.writeText(link);
    toast.success('Share link copied');
  };

  const requiresDim = ['line', 'bar', 'donut'].includes(config.chart_type);

  const chart = useMemo(() => {
    if (!preview) return null;
    if (config.chart_type === 'table') return <TableChart series={preview.series} summary={preview.summary} />;
    if (config.chart_type === 'kpi')   return <KpiTilesChart summary={preview.summary} />;
    if (config.chart_type === 'line')  return <LineBarChart series={preview.series} kind="line" />;
    if (config.chart_type === 'bar')   return <LineBarChart series={preview.series} kind="bar" />;
    if (config.chart_type === 'donut') return <DonutChart series={preview.series} />;
    return null;
  }, [preview, config.chart_type]);

  const metricsByCategory = useMemo(() => {
    const byCat = {};
    catalog.metrics.forEach((m) => { (byCat[m.category] = byCat[m.category] || []).push(m); });
    return byCat;
  }, [catalog.metrics]);

  return (
    <ReportsLayout>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap" data-testid="builder-toolbar">
        {/* Saved views along the top (D5): the last one used opens first. */}
        <SavedViewChips
          views={views.filter((v) => v.version !== 2)}
          activeId={activeView?.id}
          dirty={!!activeView && changedSinceSaved(activeView.config, config, sites)}
          onOpen={onLoad}
          onSave={() => { setScheduleAfterSave(false); setSaveOpen(true); }}
          onUpdate={onUpdate}
          onRename={onRename}
          onPin={onTogglePin}
          onDelete={onDelete}
          testId="builder-views"
          saveTestId="builder-save-btn"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={onShare} data-testid="builder-share-btn">
            <Share2 size={13} />
            Share
          </Button>
          {canSchedule && (
            <Button
              variant="outline" size="sm" className="h-9 gap-1.5"
              onClick={onSchedule}
              data-testid="builder-schedule-btn"
            >
              <Calendar size={13} />
              Schedule
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* Left rail: metrics + dimensions */}
        <div className="xl:col-span-3 space-y-4" data-testid="builder-left-rail">
          <div className="bg-white border border-hairline rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-hairline">
              <h3 className="text-[12px] font-semibold text-ink">Metrics</h3>
              <p className="text-[12.5px] text-ink-soft">{config.metrics.length} selected</p>
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              {Object.entries(metricsByCategory).map(([cat, list]) => (
                <div key={cat}>
                  <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider font-semibold text-ink-faint">{cat}</div>
                  {list.map((m) => {
                    const active = config.metrics.includes(m.key);
                    return (
                      <button
                        key={m.key}
                        onClick={() => toggleMetric(m.key)}
                        className={`w-full flex items-center justify-between px-4 py-2 text-[12px] border-l-2 transition-colors ${
                          active ? 'border-verlo bg-signal-info-tint/40 text-ink font-semibold' : 'border-transparent text-ink-soft hover:bg-hover-tint'
                        }`}
                        data-testid={`builder-metric-${m.key}`}
                      >
                        <span>{m.label}</span>
                        <span className="text-[11px] uppercase text-ink-faint font-mono-display">{m.format}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-hairline rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-hairline">
              <h3 className="text-[12px] font-semibold text-ink">Group by</h3>
              <p className="text-[12.5px] text-ink-soft">{requiresDim ? 'Required for this chart' : 'Optional'}</p>
            </div>
            <ul>
              {catalog.dimensions.map((d) => (
                <li key={d.key}>
                  <button
                    onClick={() => setConfig((c) => ({ ...c, dimension: c.dimension === d.key ? null : d.key }))}
                    className={`w-full flex items-center justify-between px-4 py-2 text-[12px] border-l-2 transition-colors ${
                      config.dimension === d.key ? 'border-verlo bg-signal-info-tint/40 text-ink font-semibold' : 'border-transparent text-ink-soft hover:bg-hover-tint'
                    }`}
                    data-testid={`builder-dim-${d.key}`}
                  >
                    <span>{d.label}</span>
                    <span className="text-[11px] uppercase text-ink-faint font-mono-display">{d.buckets}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Center: canvas */}
        <div className="xl:col-span-6 space-y-4" data-testid="builder-canvas">
          <div className="bg-white border border-hairline rounded-lg p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="text-[12px] font-semibold text-ink">Preview</h3>
                <p className="text-[12.5px] text-ink-soft">
                  {config.metrics.length} metric(s)
                  {config.dimension && ` · grouped by ${catalog.dimensions.find((d) => d.key === config.dimension)?.label}`}
                  {' · '}{formatRange(config.range)}
                </p>
              </div>
              {/* Chart picker */}
              <div className="flex gap-1 p-1 bg-paper rounded-lg">
                {Object.entries(CHART_ICONS).map(([k, { icon: Icon, label }]) => (
                  <button
                    key={k}
                    onClick={() => setConfig((c) => ({ ...c, chart_type: k }))}
                    className={`px-2.5 py-1.5 rounded-md text-[12.5px] flex items-center gap-1.5 transition-colors ${
                      config.chart_type === k ? 'bg-white text-verlo  font-semibold' : 'text-ink-soft hover:text-ink'
                    }`}
                    title={label}
                    data-testid={`builder-chart-${k}`}
                  >
                    <Icon size={12} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {previewing && <div className="text-[12px] text-ink-faint py-12 text-center">Generating preview…</div>}
            {!previewing && preview && chart}
            {!previewing && !preview && (
              <div className="text-[12px] text-ink-faint py-12 text-center" data-testid="builder-empty">
                <Sparkles size={28} className="mx-auto mb-2 text-ink-faint" />
                Pick at least one metric to preview.
              </div>
            )}
          </div>
        </div>

        {/* Right: filters */}
        <div className="xl:col-span-3 space-y-4" data-testid="builder-right-rail">
          <div className="bg-white border border-hairline rounded-lg p-4">
            <h3 className="text-[12px] font-semibold text-ink mb-3">Filters</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-[12.5px] text-ink-soft">Time range</Label>
                <Select value={config.range} onValueChange={(v) => setConfig((c) => ({ ...c, range: v }))}>
                  <SelectTrigger className="h-9 mt-1" data-testid="builder-range-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(catalog.ranges || ['today', 'wtd', 'mtd', 'ytd']).map((r) => (
                      <SelectItem key={r} value={r}>{formatRange(r)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12.5px] text-ink-soft">Site</Label>
                <SitePicker sites={sites} value={config.site} onChange={(v) => setConfig((c) => ({ ...c, site: v }))} triggerClassName="h-9 mt-1" testId="builder-site-select" />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="builder-save-dialog">
          <DialogHeader>
            <DialogTitle>Save this view</DialogTitle>
            <DialogDescription>
              {scheduleAfterSave
                ? 'Give it a name first. Then pick when it goes out, and to whom.'
                : 'Give it a name so the team can find it again, pin it to Executive, or schedule it to arrive by email.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="view-name" className="text-[12px]">View name</Label>
            <Input
              id="view-name"
              placeholder="e.g. Wednesday lunch revenue by category"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              data-testid="builder-save-name"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={onSave} disabled={saving} data-testid="builder-save-confirm">
              {saving ? 'Saving…' : (scheduleAfterSave ? 'Save and schedule' : 'Save view')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </ReportsLayout>
  );
};

export default ReportBuilderPage;
